import React from "react";
import styled from "styled-components";
import { Monitor, X, Minus, Lock, MousePointerClick } from "lucide-react";
import socket from "../../../services/socket";
import { webURL } from "../../../services/API";

export type ComputerUsePreviewFrame = {
   projectId?: string;
   nodeId?: string;
   nodeIndex?: string;
   processId?: string;
   operation?: string;
   step?: number;
   streamUrl?: string;
   label?: string;
   reason?: string;
   kind?: string;
   waiting?: boolean;
   waitingForUser?: boolean;
   takeover?: boolean;
   done?: boolean;
   screenshotJpeg?: string;
   desktopProvider?: string;
};

export const COMPUTER_USE_PREVIEW_EVENT = "dragify-computer-use-preview";

export function pushComputerUsePreview(frame: ComputerUsePreviewFrame) {
   window.dispatchEvent(new CustomEvent(COMPUTER_USE_PREVIEW_EVENT, { detail: frame }));
}

const ACTION_COPY: Record<string, string> = {
   starting: "Starting desktop…",
   observe: "Watching the desktop",
   open: "Opening",
   click: "Clicking",
   double_click: "Double-clicking",
   type: "Typing",
   keypress: "Pressing keys",
   scroll: "Scrolling",
   drag: "Dragging",
   wait: "Waiting",
   read_file: "Reading file",
   import_file: "Importing file",
   export_file: "Exporting file",
   list_files: "Listing files",
   read_document: "Reading document",
   write_document: "Writing document",
   convert_document: "Converting document",
   needs_user: "Needs your confirmation",
   take_control: "You have desktop control",
   done: "Finished",
   error: "Desktop failed to start",
};

const Panel = styled.div<{ $minimized: boolean }>`
   position: fixed;
   right: 16px;
   bottom: 16px;
   z-index: 5000;
   width: ${p => p.$minimized ? "280px" : "min(760px, calc(100vw - 28px))"};
   max-height: calc(100vh - 88px);
   display: flex;
   flex-direction: column;
   background: #0b1220;
   color: #e2e8f0;
   border-radius: 16px;
   overflow: hidden;
   border: 1px solid rgba(99, 102, 241, 0.5);
`;

const Chrome = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 10px 12px;
   background: #0f172a;
   border-bottom: 1px solid rgba(148, 163, 184, 0.18);
`;

const Title = styled.div`
   font-size: 12px;
   font-weight: 700;
   color: #f8fafc;
   display: flex;
   align-items: center;
   gap: 6px;
`;

const ActionChip = styled.span`
   flex-shrink: 0;
   font-size: 11px;
   font-weight: 700;
   text-transform: uppercase;
   background: #6366f1;
   color: #fff;
   border-radius: 999px;
   padding: 4px 8px;
`;

const UrlBar = styled.div`
   flex: 1;
   min-width: 0;
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 12px;
   background: #020617;
   border: 1px solid rgba(148, 163, 184, 0.18);
   border-radius: 999px;
   padding: 7px 12px;
   color: #cbd5e1;
`;

const UrlText = styled.div`
   min-width: 0;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
`;

const IconBtn = styled.button`
   border: 0;
   background: transparent;
   color: #94a3b8;
   cursor: pointer;
   width: 28px;
   height: 28px;
   border-radius: 8px;
   display: flex;
   align-items: center;
   justify-content: center;
   &:hover { background: rgba(148, 163, 184, 0.12); color: #fff; }
`;

const Status = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 8px 14px;
   font-size: 12px;
   color: #c7d2fe;
   background: rgba(99, 102, 241, 0.12);
   border-bottom: 1px solid rgba(99, 102, 241, 0.18);
`;

const Stage = styled.div`
   position: relative;
   background: #020617;
   aspect-ratio: 1024 / 720;
   max-height: calc(100vh - 260px);
`;

const DesktopFrame = styled.iframe<{ $interactive?: boolean }>`
   display: block;
   width: 100%;
   height: 100%;
   border: 0;
   background: #020617;
   pointer-events: ${p => p.$interactive ? "auto" : "none"};
`;

const DesktopShot = styled.img`
   display: block;
   width: 100%;
   height: 100%;
   object-fit: contain;
   background: #020617;
   pointer-events: none;
`;

const Empty = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   gap: 10px;
   min-height: 220px;
   padding: 24px;
   font-size: 14px;
   color: #94a3b8;
   text-align: center;
`;

const Actions = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 8px;
   padding: 10px 12px 12px;
   background: #020617;
`;

const ActionBtn = styled.button<{ $danger?: boolean; $primary?: boolean }>`
   border: 0;
   cursor: pointer;
   border-radius: 8px;
   padding: 8px 10px;
   font-size: 12px;
   font-weight: 600;
   color: #fff;
   background: ${p => p.$danger ? "#b91c1c" : p.$primary ? "#4f46e5" : "#334155"};
`;

const Reopen = styled.button`
   position: fixed;
   right: 16px;
   bottom: 16px;
   z-index: 5000;
   display: flex;
   align-items: center;
   gap: 8px;
   border: 0;
   cursor: pointer;
   background: #0f172a;
   color: #e2e8f0;
   border-radius: 999px;
   padding: 10px 14px;
   border: 1px solid rgba(99, 102, 241, 0.5);
   font-size: 13px;
   font-weight: 600;
`;

function applyFrame(
   payload: ComputerUsePreviewFrame,
   projectId: string | null | undefined,
   setFrame: React.Dispatch<React.SetStateAction<ComputerUsePreviewFrame | null>>,
   setOpen: React.Dispatch<React.SetStateAction<boolean>>,
   setMinimized: React.Dispatch<React.SetStateAction<boolean>>,
) {
   if (projectId && payload?.projectId && payload.projectId !== projectId) return;
   if (payload?.done) {
      setFrame((prev) => prev ? { ...prev, done: true, waiting: false, waitingForUser: false, takeover: false, operation: "done" } : { ...payload, done: true });
      return;
   }
   setFrame((prev) => {
      const next = { ...(prev || {}), ...payload };
      if (payload.operation && payload.operation !== "starting" && !("reason" in payload)) {
         next.reason = undefined;
      }
      return next;
   });
   setOpen(true);
   setMinimized(false);
}

export function ComputerUsePreview({
   projectId,
   apiKey,
   isRunning,
   enabled,
}: {
   projectId?: string | null;
   apiKey?: string | null;
   isRunning?: boolean;
   enabled?: boolean;
}) {
   const [frame, setFrame] = React.useState<ComputerUsePreviewFrame | null>(null);
   const [open, setOpen] = React.useState(false);
   const [minimized, setMinimized] = React.useState(false);
   const [busy, setBusy] = React.useState(false);

   React.useEffect(() => {
      const onPreview = (payload: ComputerUsePreviewFrame) => {
         applyFrame(payload, projectId, setFrame, setOpen, setMinimized);
      };
      const onCustom = (event: Event) => {
         const detail = (event as CustomEvent<ComputerUsePreviewFrame>).detail;
         if (detail) onPreview(detail);
      };
      socket.on("computerUsePreview", onPreview);
      window.addEventListener(COMPUTER_USE_PREVIEW_EVENT, onCustom);
      return () => {
         socket.off("computerUsePreview", onPreview);
         window.removeEventListener(COMPUTER_USE_PREVIEW_EVENT, onCustom);
      };
   }, [projectId]);

   React.useEffect(() => {
      if (isRunning && enabled) {
         setOpen(true);
         setMinimized(false);
         setFrame((prev) => {
            if (prev?.streamUrl || prev?.operation === "error") return prev;
            return prev?.reason ? prev : {
               projectId: projectId || undefined,
               waiting: true,
               operation: "starting",
               done: false,
               reason: "Starting the agent desktop…",
            };
         });
      }
   }, [isRunning, enabled, projectId]);

   const sendDecision = async (decision: "continue" | "stop" | "take_control") => {
      if (!apiKey || !frame?.processId || !frame?.nodeId) return;
      setBusy(true);
      try {
         await fetch(`${webURL}/run/computer-use/decision`, {
            method: "POST",
            headers: {
               Authorization: `Bearer ${apiKey}`,
               "Content-Type": "application/json",
            },
            body: JSON.stringify({
               processId: frame.processId,
               nodeId: frame.nodeId,
               decision,
            }),
         });
      } finally {
         setBusy(false);
      }
   };

   if (!open) {
      if ((isRunning && enabled) || frame) {
         return (
            <Reopen type="button" onClick={() => setOpen(true)}>
               <Monitor size={16} />
               Agent desktop
            </Reopen>
         );
      }
      return null;
   }

   const actionKey = frame?.done ? "done" : (frame?.operation || "starting");
   const actionLabel = ACTION_COPY[actionKey] || actionKey;
   const statusText = frame?.reason || frame?.label
      ? `${actionLabel}${frame?.reason || frame?.label ? ` — ${frame?.reason || frame?.label}` : ""}`
      : actionLabel;

   return (
      <Panel $minimized={minimized}>
         <Chrome>
            <Title><Monitor size={14} /> Agent desktop</Title>
            <ActionChip>{actionKey}{frame?.step ? ` · ${frame.step}` : ""}</ActionChip>
            <UrlBar title={frame?.streamUrl}>
               <Lock size={12} />
               <UrlText>
                  {frame?.streamUrl
                     ? "Live isolated desktop"
                     : frame?.screenshotJpeg
                        ? (frame?.desktopProvider === "agent"
                           ? "My PC (Dragify Desktop)"
                           : frame?.desktopProvider === "local"
                              ? "This Windows PC"
                              : "Live desktop")
                        : frame?.operation === "error"
                           ? "Desktop failed to start"
                           : "Waiting for the desktop"}
               </UrlText>
            </UrlBar>
            <IconBtn type="button" onClick={() => setMinimized((value) => !value)} aria-label="Minimize preview">
               <Minus size={16} />
            </IconBtn>
            <IconBtn type="button" onClick={() => setOpen(false)} aria-label="Close preview">
               <X size={16} />
            </IconBtn>
         </Chrome>
         {minimized ? (
            <Empty>The isolated desktop is running. Expand to watch or take control.</Empty>
         ) : (
            <>
               <Status>
                  <MousePointerClick size={14} />
                  {statusText}
               </Status>
               {frame?.streamUrl ? (
                  <Stage>
                     <DesktopFrame
                        title="Agent desktop"
                        src={frame.streamUrl}
                        $interactive={Boolean(frame.takeover)}
                        sandbox="allow-scripts allow-same-origin allow-pointer-lock"
                     />
                  </Stage>
               ) : frame?.screenshotJpeg ? (
                  <Stage>
                     <DesktopShot src={frame.screenshotJpeg} alt="This Windows PC" />
                  </Stage>
               ) : (
                  <Empty>
                     <Monitor size={28} />
                     {frame?.operation === "error"
                        ? (frame?.reason || "Computer Use failed to start.")
                        : (frame?.reason || "Watch this panel while the agent opens the desktop.")}
                  </Empty>
               )}
               {(frame?.waitingForUser || frame?.takeover) && !frame?.done ? (
                  <Actions>
                     <ActionBtn type="button" $primary disabled={busy} onClick={() => sendDecision("continue")}>
                        Continue
                     </ActionBtn>
                     <ActionBtn type="button" disabled={busy || Boolean(frame.takeover)} onClick={() => sendDecision("take_control")}>
                        Take control
                     </ActionBtn>
                     <ActionBtn type="button" $danger disabled={busy} onClick={() => sendDecision("stop")}>
                        Stop
                     </ActionBtn>
                  </Actions>
               ) : null}
            </>
         )}
      </Panel>
   );
}
